import { Flex, Heading, Alert, Skeleton, Box } from '@chakra-ui/react';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { useGetMeshRunsQuery } from '../../requests/dataPipeline.gql.generated';
import { MeshRunTableRowType, MeshTable } from './MeshTable';
import { DateRangePicker } from 'packages/common-frontend/components/DateRangePicker';

export const DataPipelineList = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);

  const [{ startDate, endDate }, setDateRange] = React.useState<{
    startDate: Date;
    endDate: Date;
  }>({
    startDate: sevenDaysAgo,
    endDate: today,
  });

  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { data, loading, error } = useGetMeshRunsQuery({
    variables: {
      organizationId,
      startDate: commonDate.dateToFullDateString(startDate, clientTimeZone),
      endDate: commonDate.dateToFullDateString(endDate, clientTimeZone),
    },
    fetchPolicy: 'network-only',
  });

  if (loading) {
    return <Skeleton height="600px" data-testid="dataPipeline-loading" />;
  }

  if (error) {
    const errorMessage = `Error fetching Pipelines: ${error.message}`;
    return <Alert> {errorMessage} </Alert>;
  }

  const meshTable: MeshRunTableRowType[] =
    data?.getMeshRuns.map(({ meshRunId, status, startedAt, pipeline }) => ({
      meshRunId,
      status,
      startedAt: new Date(startedAt),
      version: pipeline.fileVersion,
      reportingDate: pipeline.reportingDate,
    })) || [];

  return (
    <>
      <Flex justify="space-between" alignItems="center" mb={4}>
        <Heading as="h1" size="lg">
          Data Pipelines
        </Heading>
        <Box>
          <DateRangePicker
            onSelectDateRange={(newDateRange) => {
              const [newStartDate, newEndDate] = newDateRange;
              if (newStartDate && newEndDate) {
                setDateRange({
                  startDate: newStartDate,
                  endDate: newEndDate,
                });
              }
            }}
            currentDateRange={{
              chartDataStartDate: startDate,
              chartDataEndDate: endDate,
            }}
            width="xs"
          />
        </Box>
      </Flex>
      <MeshTable data={meshTable} />
    </>
  );
};
